import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SpottedStamp } from "@/components/SpotterCertificate";
import { purchase } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";

// Shown only after a passwordless (guest) order — an optional nudge to add an
// email (so order updates arrive by mail) and, if they want, a password for faster
// logins. Email alone is enough; a password is never required. Never blocks anything.
const GUEST_EMAIL_RX = /@guest\.thespotters\.in$/i;

function SecureAccount() {
  const { user, setUser } = useAuth();
  const hasRealEmail = user?.email && !GUEST_EMAIL_RX.test(user.email);
  const [email, setEmail] = useState(hasRealEmail ? user.email : "");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  if (!user?.passwordless || done) return null;

  const submit = async (e) => {
    e.preventDefault();
    const em = email.trim();
    if (!em && !pw) { toast.error("Add your email (or a password) to continue"); return; }
    if (em && !/^\S+@\S+\.\S+$/.test(em)) { toast.error("Enter a valid email"); return; }
    if (pw && pw.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const payload = {};
      if (em) payload.email = em;
      if (pw) payload.password = pw;
      const { data } = await api.post("/auth/complete-account", payload);
      setUser({ ...user, ...data, passwordless: pw ? false : user.passwordless });
      setDone(true);
      toast.success(pw
        ? "Saved — sign in with your email & password next time"
        : "Email saved — order updates will arrive in your inbox");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-8 border border-foreground p-6 md:p-8">
      <p className="font-mono text-[10px] uppercase tracking-widest text-[hsl(var(--accent))]">Optional</p>
      <h2 className="font-serif text-3xl tracking-tighter mt-1">Get updates by email.</h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-md">
        Your order is confirmed against your mobile number. Add your email to get order &amp; delivery
        updates by mail — no password needed. Want faster logins? Set a password too (optional).
      </p>
      <form onSubmit={submit} className="mt-5 grid sm:grid-cols-2 gap-3 max-w-lg">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email (for order updates)"
          className="underline-input" autoComplete="email" />
        <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Password (optional)"
          className="underline-input" autoComplete="new-password" />
        <div className="sm:col-span-2 flex items-center gap-4 mt-1">
          <button type="submit" disabled={busy}
            className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))] disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" onClick={() => setDone(true)}
            className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground">
            Maybe later
          </button>
        </div>
      </form>
    </div>
  );
}

export default function OrderConfirmed() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  useEffect(() => {
    api.get(`/orders/${id}`).then((r) => {
      setOrder(r.data);
      // Fire the GA4 purchase event once per order (guard against re-renders / revisits).
      try {
        const key = `ts_purchase_${r.data.order_no || r.data.id}`;
        if (!sessionStorage.getItem(key)) { purchase(r.data); sessionStorage.setItem(key, "1"); }
      } catch { purchase(r.data); }
    }).catch(() => {});
  }, [id]);
  if (!order) return <div className="p-24 font-mono text-sm">Loading receipt...</div>;
  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
      <div className="border-2 border-foreground bg-background tag-punch pl-16 pr-10 py-10 relative">
        <div className="absolute top-8 right-8"><SpottedStamp /></div>
        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          Inspection Tag · Receipt № {order.id.slice(0, 8).toUpperCase()}
        </p>
        <h1 className="font-serif text-6xl md:text-7xl mt-4 tracking-tighter leading-none">
          Received. Queued.
        </h1>
        <p className="mt-4 text-muted-foreground max-w-md">
          Your order is confirmed. The seller is preparing your parcel — you'll get an update at every step until it reaches you.
        </p>

        <div className="tick-row my-8" />

        <div className="grid grid-cols-2 gap-y-4 font-mono text-xs">
          <p className="text-muted-foreground uppercase tracking-widest">Buyer</p><p>{order.buyer_name}</p>
          <p className="text-muted-foreground uppercase tracking-widest">Items</p><p>{order.items.length}</p>
          <p className="text-muted-foreground uppercase tracking-widest">Subtotal</p><p>₹ {order.subtotal.toLocaleString("en-IN")}</p>
          <p className="text-muted-foreground uppercase tracking-widest">GST 18%</p><p>₹ {order.gst.toFixed(2)}</p>
          <p className="text-muted-foreground uppercase tracking-widest">Shipping</p><p>{order.shipping === 0 ? "FREE" : `₹ ${order.shipping}`}</p>
          <p className="uppercase tracking-widest font-semibold">Total</p><p className="font-semibold">₹ {order.total.toLocaleString("en-IN")}</p>
        </div>

        <div className="tick-row my-8" />

        <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-3">What happens next</p>
        <ol className="space-y-2 text-sm">
          <li>1. The seller confirms and packs your order</li>
          <li>2. It's dispatched and ships directly to your address</li>
          <li>3. Track every step from your orders until it's delivered</li>
        </ol>
        <p className="mt-4 text-xs text-muted-foreground">
          Every product was inspected against a reference sample before it was listed — that's what the Spotter Certified stamp means.
        </p>

        <div className="mt-10 flex gap-4">
          <Link to="/account" data-testid="track-order" className="bg-foreground text-background px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-[hsl(var(--accent))]">
            Track Order
          </Link>
          <Link to="/shop" className="border border-foreground px-6 py-3 font-mono text-xs uppercase tracking-widest hover:bg-foreground hover:text-background">
            Keep Shopping
          </Link>
        </div>
      </div>

      <SecureAccount />
    </div>
  );
}
